import { ANCHOR_TYPE, REPEAT_MODE, TARGETING_ATTRIBUTE } from '../constants'
import { END_RULE_TYPE } from '../constants/endRulesTypes'

const visitRefsFromAnchor = anchor =>
  anchor?.visitRefs?.length
    ? anchor.visitRefs
    : (anchor?.instanceIds?.length ? anchor.instanceIds : [anchor?.instanceId])
        .filter(Boolean)
        .map(visitId => ({ visitId: String(visitId), armId: '' }))

/** Arm ids implied by the visit anchor (expire visit is chosen separately per arm). */
export const buildDefaultByArmFromAnchors = (schedule, entityLookup) => {
  const byArm = {}
  visitArmsFromSchedule(schedule, entityLookup).forEach(armId => {
    byArm[String(armId)] = {}
  })
  return byArm
}

const resolveArmIdKey = (key, entityLookup) => {
  const arms = entityLookup?.arms || []
  if (arms.some(arm => String(arm.id) === String(key))) return String(key)
  const byName = arms.find(arm => arm.name === key)
  return byName ? String(byName.id) : String(key)
}

/** Fill missing per-arm ending visits from anchor visits; drop arms no longer on the anchor. */
export const syncEndRuleByArmWithAnchors = (schedule, entityLookup) => {
  const pinnedArms = visitAnchorArmsFromSchedule(schedule, entityLookup)
  const endRules = schedule?.endRules || { rules: [] }
  const rules = (endRules.rules || []).map(rule => {
    if (rule.type !== END_RULE_TYPE.relativeToVisit) return rule

    const nextByArm = {}
    if (pinnedArms.length) {
      pinnedArms.forEach(armId => {
        const armName = entityLookup?.arms?.find(arm => String(arm.id) === String(armId))?.name
        const existing = rule.byArm?.[armId] || (armName ? rule.byArm?.[armName] : undefined)
        if (existing?.visit_id || existing?.visitId) {
          nextByArm[armId] = {
            visit_id: String(existing.visit_id || existing.visitId),
          }
        }
      })
      return { ...rule, byArm: nextByArm }
    }

    const selectable = new Set(selectableArmIdsForEndRule(entityLookup))
    Object.entries(rule.byArm || {}).forEach(([key, entry]) => {
      const armId = resolveArmIdKey(key, entityLookup)
      if (!selectable.has(armId)) return
      if (entry?.visit_id || entry?.visitId) {
        nextByArm[armId] = { visit_id: String(entry.visit_id || entry.visitId) }
      } else {
        nextByArm[armId] = {}
      }
    })

    return { ...rule, byArm: nextByArm }
  })

  return { ...schedule, endRules: { ...endRules, rules } }
}

const visitAnchorInstanceIds = anchor => {
  if (anchor?.instanceIds?.length) return anchor.instanceIds.map(id => String(id))
  if (anchor?.instanceId) return [String(anchor.instanceId)]
  return visitRefsFromAnchor(anchor)
    .map(ref => ref.visitId || ref.visit_id)
    .filter(Boolean)
    .map(String)
}

/** Arms implied only by a visit deploy anchor (end visits are per anchored arm). */
export const visitAnchorArmsFromSchedule = (schedule, entityLookup) => {
  const arms = new Set()
  const visitAnchors = (schedule?.anchors || []).filter(anchor => anchor.type === ANCHOR_TYPE.visit)

  visitAnchors.forEach(anchor => {
    visitRefsFromAnchor(anchor).forEach(ref => {
      const visitId = ref.visitId || ref.visit_id
      const armId =
        ref.armId ||
        ref.arm_id ||
        entityLookup?.visits?.find(v => String(v.id) === String(visitId))?.armId
      if (armId) arms.add(String(armId))
    })
  })

  if (!arms.size && visitAnchors.length) {
    visitAnchors.forEach(anchor => {
      visitAnchorInstanceIds(anchor).forEach(visitId => {
        const armId = entityLookup?.visits?.find(v => String(v.id) === String(visitId))?.armId
        if (armId) arms.add(String(armId))
      })
    })
  }

  if (!arms.size && visitAnchors.length) {
    const armRow = (schedule?.targeting || []).find(
      row => row.attribute === TARGETING_ATTRIBUTE.arm && row.value,
    )
    if (armRow?.value) arms.add(String(armRow.value))
  }

  return [...arms]
}

/** Study arms that have at least one visit (for end-rule arm + visit pickers). */
export const selectableArmIdsForEndRule = (entityLookup = {}) => {
  const armsWithVisits = new Set(
    (entityLookup.visits || []).map(visit => String(visit.armId)).filter(Boolean),
  )
  return (entityLookup.arms || [])
    .map(arm => String(arm.id))
    .filter(id => armsWithVisits.has(id))
}

/** Arms that need an ending visit for this end rule (visit anchor pins arms; else keys on the rule). */
export const endRuleArmIdsForRule = (schedule, rule, entityLookup) => {
  const pinned = visitAnchorArmsFromSchedule(schedule, entityLookup)
  if (pinned.length) return pinned

  const selectable = new Set(selectableArmIdsForEndRule(entityLookup))
  return Object.keys(rule?.byArm || {})
    .map(key => resolveArmIdKey(key, entityLookup))
    .filter(armId => selectable.has(armId))
}

/** @deprecated Prefer endRuleArmIdsForRule for validation; still used for default arm lists. */
export const endRuleArmsFromSchedule = (schedule, entityLookup) => {
  const pinned = visitAnchorArmsFromSchedule(schedule, entityLookup)
  if (pinned.length) return pinned

  const fromDeployArm = (schedule?.anchors || [])
    .filter(anchor => anchor.type === ANCHOR_TYPE.arm && anchor.instanceId)
    .map(anchor => String(anchor.instanceId))
  if (fromDeployArm.length) return fromDeployArm

  ;(schedule?.targeting || []).forEach(row => {
    if (row.attribute === TARGETING_ATTRIBUTE.arm && row.value) {
      fromDeployArm.push(String(row.value))
    }
  })
  if (fromDeployArm.length) return [...new Set(fromDeployArm)]

  return selectableArmIdsForEndRule(entityLookup)
}

/** @deprecated Prefer endRuleArmsFromSchedule */
export const visitArmsFromSchedule = endRuleArmsFromSchedule

export const deployVisitIdsFromSchedule = schedule => {
  const ids = new Set()
  ;(schedule?.anchors || []).forEach(anchor => {
    if (anchor.type !== ANCHOR_TYPE.visit) return
    visitAnchorInstanceIds(anchor).forEach(visitId => ids.add(String(visitId)))
  })
  return ids
}

export const validateEndRules = (schedule, scheduleIndex, entityLookup) => {
  const errors = {}
  if (!schedule?.repeat?.mode || schedule.repeat.mode === REPEAT_MODE.doesNotRepeat) return errors
  const endRules = schedule?.endRules || {}
  const rules = endRules.rules || []
  if (!rules.length) return errors

  const selectableArms = selectableArmIdsForEndRule(entityLookup)

  rules.forEach((rule, index) => {
    const prefix = `schedules.${scheduleIndex}.endRules.rules.${index}`
    if (!rule.type) {
      errors[`${prefix}.type`] = 'Select an end rule type.'
      return
    }
    if (rule.type === END_RULE_TYPE.afterNOccurrences) {
      if (!Number(rule.count) || Number(rule.count) < 1) {
        errors[`${prefix}.count`] = 'Enter how many occurrences end the series.'
      }
    }
    if (rule.type === END_RULE_TYPE.relativeToVisit) {
      if (selectableArms.length >= 1) {
        const visitPinned = visitAnchorArmsFromSchedule(schedule, entityLookup).length > 0
        const arms = endRuleArmIdsForRule(schedule, rule, entityLookup)

        if (!visitPinned && arms.length === 0) {
          errors[`${prefix}.byArm`] = 'Select at least one study arm for the ending visit.'
        }

        const missing = arms.filter(armId => {
          const armName =
            entityLookup?.arms?.find(arm => String(arm.id) === armId)?.name || armId
          const entry =
            rule.byArm?.[armId] ||
            rule.byArm?.[armName] ||
            rule.byArm?.[resolveArmIdKey(armId, entityLookup)]
          return !entry?.visit_id && !entry?.visitId
        })
        if (missing.length) {
          missing.forEach(armId => {
            const armLabel = entityLookup?.arms?.find(arm => String(arm.id) === armId)?.name || armId
            errors[`${prefix}.byArm.${armId}`] =
              `${armLabel} has no ending visit yet — the schedule can't be saved until every anchored arm has one.`
          })
          if (!errors[`${prefix}.byArm`]) {
            errors[`${prefix}.byArm`] = errors[`${prefix}.byArm.${missing[0]}`]
          }
        }
      }
    }
  })
  return errors
}
